/**
 * Strategy Worker v2 Type Definitions
 * These types correspond to the Strategy Worker API contracts
 */

export type ObjectiveType = 
  | 'EASY_WIN' 
  | 'HIGH_COMP' 
  | 'SMALL_CO' 
  | 'REMOTE' 
  | 'RELOCATION';

export interface StrategyWeight {
  strategy_key: string;
  alpha: number;
  beta: number;
  weight: number;
  confidence_interval?: [number, number];
}

export interface UserObjective {
  objective: ObjectiveType;
  constraints: {
    locations?: string[];
    remote?: boolean;
    salary_floor?: number;
    seniority_levels?: string[];
    employment_types?: string[];
    industries?: string[];
    exploration_rate?: number;
    small_company_bias?: boolean;
    blocklist?: string[];
  };
}

export interface QuerySpec {
  query: string;
  filters: {
    location?: string;
    remote?: boolean;
    salary_min?: number;
    salary_max?: number;
    seniority?: string;
    employment_type?: string;
  };
  rationale: string;
  strategy_key: string;
  estimated_hit_rate?: number;
}

export interface StrategyStatus {
  objective: ObjectiveType;
  constraints: UserObjective['constraints'];
  weights: StrategyWeight[];
  sources: {
    resume_id?: string;
    tags_count?: number;
    feedback_days?: number;
  };
  performance_summary?: {
    total_events: number;
    success_rate: number;
    top_strategy: string;
  };
}

export interface SearchEvent {
  id: string;
  user_id: string;
  job_id: string;
  action: 'view' | 'save' | 'apply' | 'interview' | 'offer' | 'reject';
  strategy_key?: string;
  query_id?: string;
  reward_points: number;
  created_at: string;
}

export interface StrategyMetrics {
  strategy_key: string;
  total_events: number;
  success_events: number;
  success_rate: number;
  confidence_interval: [number, number];
  last_updated: string;
}

// Objective definitions for UI display
export const OBJECTIVE_DEFINITIONS: Record<ObjectiveType, {
  name: string;
  description: string;
  color: string;
}> = {
  EASY_WIN: {
    name: 'Easy Wins',
    description: 'Accessible opportunities with broader requirements and faster hiring',
    color: 'bg-green-500',
  },
  HIGH_COMP: {
    name: 'High Compensation',
    description: 'Senior roles with high salaries, equity packages, and top-tier companies',
    color: 'bg-amber-500',
  },
  SMALL_CO: {
    name: 'Small Companies',
    description: 'Startups and small companies with growth potential and equity focus',
    color: 'bg-blue-500',
  },
  REMOTE: {
    name: 'Remote First',
    description: 'Remote-first positions and distributed teams worldwide',
    color: 'bg-purple-500',
  },
  RELOCATION: {
    name: 'Relocation',
    description: 'Geographic relocation opportunities with visa/relocation support',
    color: 'bg-orange-500',
  },
};

// Strategy key definitions for badges
export const STRATEGY_DEFINITIONS: Record<string, {
  name: string;
  color: string;
  description: string;
}> = {
  PROFILE_STRATEGY: {
    name: 'Profile',
    color: 'bg-blue-500',
    description: 'Based on your profile preferences',
  },
  RESUME_TAGS_STRATEGY: {
    name: 'Resume Skills',
    color: 'bg-green-500', 
    description: 'Matched from your resume skills',
  },
  OBJECTIVE_HIGH_COMP_STRATEGY: {
    name: 'High Comp',
    color: 'bg-amber-500',
    description: 'High compensation targeting',
  },
  OBJECTIVE_EASY_WIN_STRATEGY: {
    name: 'Easy Win',
    color: 'bg-emerald-500',
    description: 'Accessible opportunities',
  },
  OBJECTIVE_SMALL_CO_STRATEGY: {
    name: 'Small Co',
    color: 'bg-indigo-500',
    description: 'Startup and small company focus',
  },
  EXPLORATION_STRATEGY: {
    name: 'Exploration',
    color: 'bg-purple-500',
    description: 'Emerging tech and new opportunities',
  },
  FEEDBACK_DIVERSE_STRATEGY: {
    name: 'Feedback',
    color: 'bg-pink-500',
    description: 'Based on your interaction patterns',
  },
};